// utils/dateDisplayUtils.js
import moment from 'moment';

/**
 * Gets the appropriate dates and labels for display based on user type, job state, and available data
 * @param {Object} request - The request object with availableDate, dueDate, currentState, etc.
 * @param {Array} calendarDays - Array of calendar entries for this request
 * @param {string} userType - 'dealer' or 'installer'
 * @returns {Object} { startDate, endDate, startLabel, endLabel, notification }
 */
export function getDisplayDates(request, calendarDays, userType) {
  // Filter calendar days for this specific request
  const jobCalendarDays = calendarDays.filter(day => day.requestId === request.requestId);
  
  // Get calendar dates if they exist
  const calendarDates = getCalendarDateRange(jobCalendarDays);
  
  // Determine if job is scheduled (has calendar entries)
  const isScheduled = jobCalendarDays.length > 0 && 
    ['scheduled', 'received', 'started', 'finished'].includes(request.currentState);

  if (userType === 'dealer') {
    return getDealerDisplay(request, calendarDates, isScheduled);
  } else {
    return getInstallerDisplay(request, calendarDates, isScheduled);
  }
}

/**
 * Dealer always sees their original timeline in summary
 */
function getDealerDisplay(request, calendarDates, isScheduled) {
  return {
    startDate: request.availableDate,
    endDate: request.dueDate,
    startLabel: 'Available',
    endLabel: 'Due',
    notification: null // Notifications handled in detail view
  };
}

/**
 * Installer sees calendar dates when available, original timeline for new requests
 */
function getInstallerDisplay(request, calendarDates, isScheduled) {
  if (isScheduled && calendarDates.startDate && calendarDates.endDate) {
    // Use actual calendar dates
    return {
      startDate: calendarDates.startDate,
      endDate: calendarDates.endDate,
      startLabel: 'Start',
      endLabel: 'Complete',
      notification: null
    };
  } else {
    // Use original timeline for new/unbooked requests
    const isRetailJob = request.requestType === 'installer';
    return {
      startDate: request.availableDate,
      endDate: request.dueDate,
      startLabel: isRetailJob ? 'Offered' : 'Available',
      endLabel: isRetailJob ? 'Complete By' : 'Due',
      notification: null
    };
  }
}

/**
 * Gets schedule change notification for dealer detail view
 * @param {Object} request - The request object
 * @param {Array} calendarDays - Calendar entries for this request
 * @returns {Object|null} Notification object or null
 */
export function getScheduleNotification(request, calendarDays) {
  // Only for dealers viewing scheduled jobs
  const jobCalendarDays = calendarDays.filter(day => day.request_id === request.requestId);

  
  if (jobCalendarDays.length === 0) {
    return null;
  }

  const calendarDates = getCalendarDateRange(jobCalendarDays);

  if (!calendarDates.startDate || !calendarDates.endDate) {
    return null;
  }

  const originalStart = moment(request.availableDate);
  const originalDue = moment(request.dueDate);
  const scheduledStart = moment(calendarDates.startDate);
  const scheduledEnd = moment(calendarDates.endDate);

  // Check if due date is at risk (scheduled end > due date)
  if (scheduledEnd.isAfter(originalDue)) {
    return {
      type: 'warning',
      message: `Scheduled to finish ${scheduledEnd.format('MMM DD')} - after required due date`
    };
  }

  // Check if start date moved significantly (≥2 days)
  const startDaysDiff = Math.abs(scheduledStart.diff(originalStart, 'days'));
  if (startDaysDiff >= 2) {
    return {
      type: 'info',
      message: `Rescheduled to start ${scheduledStart.format('MMM DD')} (completing by ${originalDue.format('MMM DD')})`
    };
  }

  return {
      type: 'info',
      message: `Specialist Start Date : ${scheduledStart.format('MMM DD')} - End Date : ${scheduledEnd.format('MMM DD')}`
    };
}

function getCalendarDateRange(calendarDays) {
  
  const activeEntries = calendarDays.filter(day => 
    ['booked', 'pending', 'started', 'finished', 'completed'].includes(day.work_status)
  );

  if (activeEntries.length === 0) return { startDate: null, endDate: null };

  let jobStartDate = null;
  let jobEndDate = null;

  activeEntries.forEach(entry => {
    const serviceStart = moment(entry.work_date);
    
    // For durations < 1 day, end date is same as start date
    // For durations >= 1 day, add (duration - 1) days
    const serviceEnd = entry.daily_work < 1 
      ? serviceStart.clone()
      : serviceStart.clone().add(entry.daily_work - 1, 'days');
    
    // Track earliest start
    if (!jobStartDate || serviceStart.isBefore(jobStartDate)) {
      jobStartDate = serviceStart;
    }
    
    // Track latest completion
    if (!jobEndDate || serviceEnd.isAfter(jobEndDate)) {
      jobEndDate = serviceEnd;
    }
  });

  return {
    startDate: jobStartDate ? jobStartDate.format('YYYY-MM-DD') : null,
    endDate: jobEndDate ? jobEndDate.format('YYYY-MM-DD') : null
  };
}

/**
 * Helper function to format dates consistently
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatDisplayDate(date) {
  if (!date) return '';
  return moment(date).format('MMM DD, YYYY');
}

/**
 * Helper function to check if a date is valid
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is valid
 */
export function isValidDate(date) {
  return date && moment(date).isValid();
}